import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Drawer,
  drawerClasses,
  styled,
  SvgIcon,
  type DrawerProps,
} from '@mui/material';
import classNames from 'classnames';
import { type PropsWithChildren } from 'react';

import { EAircraftStatus, MAX_DRAWER_WIDTH, MIN_DRAWER_WIDTH } from '#shared';

import { getAircraftStatus, getAircraftTimeSeverity } from '../../helpers';

import { AircraftDrawerAircraftEvents } from './AircraftDrawer.AircraftEvents';
import { AircraftDrawerDutyEvents } from './AircraftDrawer.DutyEvents';
import { AircraftDrawerLocation } from './AircraftDrawer.Location';
import { AircraftDrawerTimeline } from './AircraftDrawer.Timeline';
import { AircraftDrawerWeather } from './AircraftDrawer.Weather';
import { AircraftDrawerProvider } from './AircraftDrawerContext';

import type { AircraftData } from '#entities';

const PREFIX = 'TSMED-AircraftDrawer';

export const aircraftDrawerClasses = {
  container: `${PREFIX}-container`,
  close: `${PREFIX}-close`,
  header: `${PREFIX}-header`,
  headerIcon: `${PREFIX}-headerIcon`,
  headerRegistration: `${PREFIX}-headerRegistration`,
  status: `${PREFIX}-status`,
  aircraftEvents: `${PREFIX}-aircraftEvents`,
  aircraftEvent: `${PREFIX}-aircraftEvent`,
  aircraftEventLabel: `${PREFIX}-aircraftEventLabel`,
  aircraftEventTime: `${PREFIX}-aircraftEventTime`,
};

const Root = styled(Drawer)(
  ({ theme: { spacing, palette, mixins, utils, breakpoints } }) => ({
    background: 'none',
    width: utils.clamp(MIN_DRAWER_WIDTH, MAX_DRAWER_WIDTH),
    flexShrink: 0,
    ...mixins.aircraftStatusStyles(palette),
    ...mixins.aircraftStatusStyles(palette, { prefix: '&' }),
    ...mixins.severityStyles(palette),
    ...mixins.severityStyles(palette, { prefix: '&' }),
    ...mixins.organStyles(palette),

    [breakpoints.down('sm')]: {
      width: '100%',
    },

    '& .MuiDrawer-paper': {
      width: utils.clamp(MIN_DRAWER_WIDTH, MAX_DRAWER_WIDTH),
      boxSizing: 'border-box',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',

      '&::-webkit-scrollbar': {
        display: 'none',
      },

      [breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    [`.${aircraftDrawerClasses.close}`]: {
      alignSelf: 'flex-end',
      color: palette.common.white,
      cursor: 'pointer',
    },
    [`.${drawerClasses.paper}`]: {
      display: 'flex',
      // flexDirection: 'column',
      background: 'black',
      boxSizing: 'border-box',
      border: 'none',
      borderRadius: spacing(0.5),
    },
    [`.${aircraftDrawerClasses.container}`]: {
      // ...mixins.severityStyles(palette),
      // ...mixins.aircraftStatusStyles(palette),
      // ...mixins.aircraftStatusStyles(palette, { prefix: '&' }),
      // ...mixins.organStyles(palette),
      display: 'flex',
      height: '100%',
      // border: '1px solid',
      // borderRadius: spacing(0.5),
      flexDirection: 'column',

      gap: spacing(2),
      padding: spacing(2),
      color: 'var(--primaryColor)',
      fontFamily: "'JetBrains Mono', monospace",
      background: 'black',
    },

    [`.${aircraftDrawerClasses.header}`]: {
      color: 'inherit',
      display: 'flex',
      alignItems: 'center',
      gap: spacing(0.5),

      [`.${aircraftDrawerClasses.headerRegistration}`]: {
        fontSize: utils.clamp(16, 32),
      },
      [`.${aircraftDrawerClasses.status}`]: {
        background: 'none',
        color: 'var(--primaryColor)',
      },
    },
    [`.${aircraftDrawerClasses.aircraftEvents}`]: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing(1),
      [`.${aircraftDrawerClasses.aircraftEvent}`]: {
        display: 'flex',
        gap: spacing(1),
        [`.${aircraftDrawerClasses.aircraftEventLabel},.${aircraftDrawerClasses.aircraftEventTime}`]:
          {
            lineHeight: spacing(4),
            padding: spacing(0.5, 1),
          },
        [`.${aircraftDrawerClasses.aircraftEventLabel}`]: {
          width: 180,
        },
        [`.${aircraftDrawerClasses.aircraftEventTime}`]: {
          sup: {
            lineHeight: 0,
          },
        },
      },
    },
  }),
);

export interface AircraftDrawerProps extends Omit<DrawerProps, 'children'> {
  aircraftData: AircraftData;
  onClose?: () => void;
  open?: boolean;
  children?: ((props: AircraftData) => React.ReactNode) | React.ReactNode;
}

export const AircraftDrawer = ({
  aircraftData,
  children,
  onClose,
  ...drawerProps
}: PropsWithChildren<AircraftDrawerProps>) => (
  <AircraftDrawerProvider
    aircraftData={aircraftData}
    open={drawerProps.open}
    selectedAirport={
      aircraftData?.airportLastKnown
        ? {
            Id: aircraftData.id,
            ICAO: aircraftData.airportLastKnown.ICAO,
            IanaTimeZone: aircraftData.airportLastKnown.IanaTimeZone,
          }
        : undefined
    }
  >
    {(aircraftData, { selectAirport }) => {
      const handleClose = () => {
        selectAirport();
        onClose?.();
      };
      const { Registration, maintenanceType, actualOrgan } = aircraftData;
      const actualTimeSeverity = getAircraftTimeSeverity(aircraftData);
      const actualStatus = getAircraftStatus(aircraftData);
      return (
        <Root
          {...drawerProps}
          onClose={handleClose}
          anchor="right"
          variant="temporary"
          ModalProps={{
            keepMounted: true,
          }}
        >
          <Box
            className={classNames(
              aircraftDrawerClasses.container,
              `aircraft-status-${actualStatus}`,
              // `severity-status-${actualTimeSeverity}`,
              {
                [`aircraft-status-${actualStatus}`]: !maintenanceType,
                [`severity-status-${actualTimeSeverity}`]: [
                  EAircraftStatus.Available,
                  EAircraftStatus.OnTrip,
                ].includes(actualStatus),
                [`maintenance-type-${maintenanceType?.OpsBoardBadge}`]:
                  !!maintenanceType,
              },
            )}
          >
            <SvgIcon
              onClick={handleClose}
              className={aircraftDrawerClasses.close}
              component={CloseIcon}
            />
            <Box className={aircraftDrawerClasses.header}>
              {/* <AircraftIcon className={aircraftDrawerClasses.headerIcon} /> */}
              <Box className={aircraftDrawerClasses.headerRegistration}>
                {Registration}
              </Box>
              <Box flexGrow={1} />
              <Box
                className={classNames(aircraftDrawerClasses.status, {
                  [`organ-type-${actualOrgan}`]: actualOrgan,
                })}
              >
                {actualStatus === EAircraftStatus.NotAvailable
                  ? 'Not Available'
                  : actualStatus}
              </Box>
            </Box>
            {children || (
              <>
                <AircraftDrawerAircraftEvents />
                <AircraftDrawerLocation />
                <AircraftDrawerDutyEvents />
                <AircraftDrawerTimeline />
                <AircraftDrawerWeather />
              </>
            )}
          </Box>
        </Root>
      );
    }}
  </AircraftDrawerProvider>
);

AircraftDrawer.AircraftEvents = AircraftDrawerAircraftEvents;
AircraftDrawer.DutyEvents = AircraftDrawerDutyEvents;
AircraftDrawer.Location = AircraftDrawerLocation;
AircraftDrawer.Timeline = AircraftDrawerTimeline;
AircraftDrawer.Weather = AircraftDrawerWeather;
